'use client'

/**
 * What is waiting on a person right now.
 *
 * ## Why this is derived rather than stored
 *
 * There is no notification table and this component does not want one. Every
 * item below is a *view* of state that already exists and is already
 * authoritative: a gate is open and nobody has decided it, a case has been held
 * past its service level, a drafted letter is sitting unapproved. Persisting a
 * second copy of those facts would create the one thing a Command Center must
 * not have — a bell that says three things need attention while the queue says
 * five, with no way to tell which is right.
 *
 * So the source is `/api/dashboard`, the same payload the main screen reads,
 * and "read" is a local dismissal of an item the reviewer has looked at rather
 * than a claim about the underlying work. The work is not done because somebody
 * dismissed a notification about it, and the count returns on the next load if
 * the gate is still open.
 *
 * ## What it used to be
 *
 * A hardcoded array of three: a diagnostics run that never ran, a feature
 * announcement for a product this is not, and a token expiry warning computed
 * from nothing. It rendered to users, with a red unread badge, in the header of
 * every screen. A control that manufactures urgency is worse than no control,
 * because the one time something genuinely needs attention it will look exactly
 * like the three things that never did.
 */

import * as React from 'react'
import { apiClient } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { GATE_LABEL, label } from '@/lib/labels'
import type { DashboardOut } from '@/types/uic-dashboard'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

/**
 * How urgent an item is.
 *
 * Deliberately three, not four. `success` existed to carry the diagnostics
 * message and nothing real produces one: a Command Center notification exists
 * because something is waiting, and nothing waits on good news.
 */
type NotificationType = 'info' | 'warning' | 'error'

interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  /** Where clicking takes the reviewer. Every item leads to the work itself. */
  href?: string
}

const typeConfig: Record<
  NotificationType,
  { icon: React.ElementType; color: string; bg: string }
> = {
  info: { icon: Icons.info, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  warning: {
    icon: Icons.alertTriangle,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
  error: {
    icon: Icons.alertCircle,
    color: 'text-destructive',
    bg: 'bg-destructive/10',
  },
}

/**
 * Turn the dashboard payload into the list of things waiting on a person.
 *
 * Ordered by how much attention each deserves: cases past their service level
 * first, then gates waiting on a decision, then anything the regulatory
 * watchdog has raised that nobody has actioned.
 */
function deriveNotifications(data: DashboardOut | null): Notification[] {
  if (!data) return []
  const items: Notification[] = []

  const queue = data.gate_queue
  if (queue) {
    if (queue.over_sla > 0) {
      items.push({
        id: 'over-sla',
        type: 'error',
        title: `${queue.over_sla} case${queue.over_sla === 1 ? '' : 's'} past the review window`,
        message:
          queue.over_sla === 1
            ? 'A gate decision has been outstanding longer than the service level allows.'
            : 'These gate decisions have been outstanding longer than the service level allows.',
        href: '/queue',
      })
    }

    // One entry per gate rather than one per case: a reviewer wants to know
    // which stage is backed up, and the queue screen itself lists the cases.
    const byGate = new Map<number, number>()
    for (const item of queue.items) {
      byGate.set(item.gate_no, (byGate.get(item.gate_no) ?? 0) + 1)
    }
    for (const [gateNo, count] of [...byGate.entries()].sort(
      (a, b) => a[0] - b[0]
    )) {
      items.push({
        id: `gate-${gateNo}`,
        type: 'warning',
        title: `Gate ${gateNo} · ${count} awaiting a decision`,
        message: `${label(GATE_LABEL, String(gateNo))}. Nothing advances past this gate until a named reviewer decides.`,
        href: '/queue',
      })
    }
  }

  const open = data.watchdog?.items.filter((item) => !item.is_actioned) ?? []
  if (open.length > 0) {
    items.push({
      id: 'watchdog',
      type: 'info',
      title: `${open.length} regulatory development${open.length === 1 ? '' : 's'} to review`,
      message:
        'Each names the rule rows it bears on. The agent proposes; it never edits a rule row.',
      href: '/',
    })
  }

  return items
}

function NotificationItem({
  notification,
  onDismiss,
}: {
  notification: Notification
  onDismiss: (id: string) => void
}) {
  const config = typeConfig[notification.type]
  const Icon = config.icon
  const body = (
    <>
      <div
        className={cn(
          'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          config.bg
        )}
      >
        <Icon className={cn('h-4 w-4', config.color)} strokeWidth={1.75} />
      </div>
      <div className='min-w-0 flex-1'>
        <p className='text-sm font-medium text-foreground'>
          {notification.title}
        </p>
        <p className='mt-0.5 text-xs text-muted-foreground'>
          {notification.message}
        </p>
      </div>
    </>
  )
  return (
    <div className='flex items-start gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50'>
      {notification.href ? (
        <a
          href={notification.href}
          className='flex flex-1 items-start gap-3 rounded-lg'
          onClick={() => onDismiss(notification.id)}
        >
          {body}
        </a>
      ) : (
        <div className='flex flex-1 items-start gap-3'>{body}</div>
      )}
      <button
        type='button'
        onClick={() => onDismiss(notification.id)}
        aria-label={`Dismiss: ${notification.title}`}
        className='mt-1 shrink-0 rounded p-1 text-muted-foreground/60 transition-colors hover:text-foreground'
      >
        <Icons.close className='h-3.5 w-3.5' strokeWidth={2} />
      </button>
    </div>
  )
}

export function NotificationCenter() {
  const [data, setData] = React.useState<DashboardOut | null>(null)
  const [dismissed, setDismissed] = React.useState<Set<string>>(new Set())

  React.useEffect(() => {
    let cancelled = false
    apiClient
      .get<DashboardOut>('/api/dashboard')
      .then((payload) => {
        if (!cancelled) setData(payload)
      })
      // A bell that cannot reach the API shows nothing. It does not show an
      // error, because the reviewer is not doing anything here yet and the
      // screen they are on will surface the failure where it matters.
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const notifications = React.useMemo(
    () => deriveNotifications(data).filter((item) => !dismissed.has(item.id)),
    [data, dismissed]
  )

  const dismiss = (id: string) =>
    setDismissed((prev) => new Set(prev).add(id))

  const dismissAll = () =>
    setDismissed(new Set(notifications.map((item) => item.id)))

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='relative rounded-full text-muted-foreground hover:text-foreground'
          aria-label={
            notifications.length === 0
              ? 'Nothing is waiting on you'
              : `${notifications.length} item${notifications.length === 1 ? '' : 's'} waiting on you`
          }
        >
          <Icons.bell className='h-5 w-5' strokeWidth={1.5} />
          {notifications.length > 0 && (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5',
                'flex h-4 min-w-4 items-center justify-center',
                'rounded-full bg-destructive px-1',
                'text-[10px] font-semibold text-white',
                'animate-badge-bounce'
              )}
            >
              {notifications.length > 9 ? '9+' : notifications.length}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='w-80 p-0'>
        <div className='flex items-center justify-between border-b border-border/50 px-4 py-3'>
          <h3 className='font-display font-semibold text-foreground'>
            Waiting on you
          </h3>
          {notifications.length > 0 && (
            <Button
              variant='link'
              size='sm'
              onClick={dismissAll}
              className='h-auto p-0 text-xs text-brand-cornflower'
            >
              Dismiss all
            </Button>
          )}
        </div>

        <div className='max-h-[300px] overflow-y-auto p-2'>
          {notifications.length === 0 ? (
            <div className='py-8 text-center'>
              <div className='mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted/50'>
                <Icons.checkCircle className='h-6 w-6 text-muted-foreground' />
              </div>
              <p className='text-sm font-medium text-foreground'>
                Nothing is waiting on you
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                No gate is holding a case for a decision.
              </p>
            </div>
          ) : (
            <div className='space-y-1'>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onDismiss={dismiss}
                />
              ))}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className='border-t border-border/50 p-2'>
            <a
              href='/queue'
              className={cn(
                'flex h-9 w-full items-center justify-center rounded-md',
                'text-sm font-medium text-brand-navy',
                'transition-colors hover:bg-muted/50'
              )}
            >
              Open the review queue
            </a>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
